import {
  HAIRSTYLE_CONFIGS,
  getHairstyleConfig,
  type HairstyleConfig,
} from '../config/hairstyles';
import type { HairQuizAnswers, HairQuizVariant } from '../types';

const QUIZ_RESULTS_COUNT = 3;

const getVariantStyleId = (variant: HairQuizVariant): string => variant.style_id || variant.id || '';

const createCatalogVariant = (style: HairstyleConfig): HairQuizVariant => ({
  id: style.id,
  style_id: style.id,
  title: style.name.ru,
  description: style.description.ru,
  image_prompt_en: style.promptHint,
});

const similarityScore = (candidate: HairstyleConfig, selected: HairstyleConfig[]): number => {
  if (!selected.length) return 0;

  return selected.reduce((score, style) => {
    if (candidate.length && candidate.length === style.length) score += 4;
    if (candidate.vibe && candidate.vibe === style.vibe) score += 2;
    return score;
  }, 0);
};

export const normalizeQuizRecommendations = (variants: HairQuizVariant[]): HairQuizVariant[] => {
  const uniqueVariants: HairQuizVariant[] = [];
  const selectedIds = new Set<string>();

  for (const variant of variants) {
    const style = getHairstyleConfig(getVariantStyleId(variant));
    if (!style || selectedIds.has(style.id)) continue;

    selectedIds.add(style.id);
    uniqueVariants.push({ ...variant, id: style.id, style_id: style.id });
    if (uniqueVariants.length === QUIZ_RESULTS_COUNT) return uniqueVariants;
  }

  const selectedStyles = uniqueVariants
    .map((variant) => getHairstyleConfig(getVariantStyleId(variant)))
    .filter((style): style is HairstyleConfig => Boolean(style));

  const complementaryStyles = HAIRSTYLE_CONFIGS
    .filter((style) => !style.hiddenInCatalog && !selectedIds.has(style.id))
    .map((style, catalogIndex) => ({
      style,
      catalogIndex,
      score: similarityScore(style, selectedStyles),
    }))
    .sort((left, right) => right.score - left.score || left.catalogIndex - right.catalogIndex);

  for (const { style } of complementaryStyles) {
    uniqueVariants.push(createCatalogVariant(style));
    if (uniqueVariants.length === QUIZ_RESULTS_COUNT) break;
  }

  return uniqueVariants.slice(0, QUIZ_RESULTS_COUNT);
};

const LOCAL_QUIZ_RULES: Array<[keyof HairQuizAnswers, string, string[]]> = [
  ['styling_direction', 'back', ['slick_back', 'pompadour', 'quiff', 'flow_cut']],
  ['styling_direction', 'forward', ['textured_crop', 'crop', 'caesar', 'fringe']],
  ['styling_direction', 'side', ['side_part', 'taper_fade', 'classic_long']],
  ['styling_direction', 'none', ['buzz', 'fade', 'crop']],
  ['fringe_preference', 'yes', ['fringe', 'textured_crop', 'curtains', 'caesar']],
  ['fringe_preference', 'no', ['fade', 'slick_back', 'pompadour', 'buzz']],
  ['top_length', 'short', ['buzz', 'fade', 'caesar', 'crop']],
  ['top_length', 'medium', ['textured_crop', 'quiff', 'side_part', 'taper_fade']],
  ['top_length', 'long', ['wolf_cut', 'flow_cut', 'curtains', 'classic_long']],
  ['sides_style', 'skin', ['fade', 'undercut', 'buzz']],
  ['sides_style', 'short_fade', ['taper_fade', 'textured_crop', 'crop']],
  ['sides_style', 'medium', ['side_part', 'classic_long', 'curtains']],
  ['sides_style', 'long_sides', ['flow_cut', 'curtains', 'classic_long']],
  ['sides_style', 'natural', ['flow_cut', 'curtains', 'messy_style']],
  ['styling_time', 'minimal', ['buzz', 'fade', 'crop', 'caesar']],
  ['styling_time', 'moderate', ['textured_crop', 'side_part', 'taper_fade']],
  ['styling_time', 'extended', ['pompadour', 'slick_back', 'quiff', 'wolf_cut']],
  ['hair_type', 'curly', ['curly_volume', 'messy_style', 'flow_cut']],
  ['hair_type', 'wavy', ['flow_cut', 'curtains', 'messy_style']],
  ['face_shape', 'round', ['quiff', 'pompadour', 'undercut']],
  ['face_shape', 'rectangular', ['side_part', 'curtains', 'classic_long']],
  ['beard', 'full', ['fade', 'undercut', 'slick_back']],
];

export const getLocalQuizRecommendations = (answers: HairQuizAnswers): HairQuizVariant[] => {
  const scores = new Map(HAIRSTYLE_CONFIGS.map((style) => [style.id, 0]));

  for (const [field, expectedValue, styleIds] of LOCAL_QUIZ_RULES) {
    if (answers[field] !== expectedValue) continue;
    styleIds.forEach((styleId, index) => {
      if (scores.has(styleId)) scores.set(styleId, (scores.get(styleId) ?? 0) + Math.max(5 - index, 1));
    });
  }

  const ranked = HAIRSTYLE_CONFIGS
    .filter((style) => !style.hiddenInCatalog)
    .map((style, catalogIndex) => ({ style, catalogIndex, score: scores.get(style.id) ?? 0 }))
    .sort((left, right) => right.score - left.score || left.catalogIndex - right.catalogIndex)
    .slice(0, QUIZ_RESULTS_COUNT)
    .map(({ style }) => createCatalogVariant(style));

  return normalizeQuizRecommendations(ranked);
};
